import $ from "../../libs/jquery-3.3.1.min.js"
import {BaseMiniGame} from "./BaseMiniGame";
import * as PIXI from "pixi.js";
import {gsap} from "../../libs/gsap/all.js";
import {AppearingInteractiveButton} from "./components/AppearingInteractiveButton";

// Game description : Slider with steps outside image to find an object
export class GameC extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);

    // Canvas/Background sizes
    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;
    this.backgroundWidth = this.data.backgroundDimensions.width;
    this.backgroundHeight = this.data.backgroundDimensions.height;
    this.appPadding = 85; // empty space that surround the game

    // Initial positions
    this.initialSliderHandlePos = {x: 0, y: 0};
    this.initialHandleStep = {
      "index": 0,
      "percentage": this.data.sliderSteps[0].percentage
    };

    // Handle steps
    this.sliderSteps = this.data.sliderSteps;
    this.sliderStepSprites = [];
    this.currentHandleStep = {...this.initialHandleStep};
    this.sliderHandleWidth = 26;
    this.sliderHandleHalfWidth = this.sliderHandleWidth / 2;
    this.sliderLabelsTextStyle = new PIXI.TextStyle({
      fontFamily: "Sketchnotetext Regular",
      fontSize: 22,
      lineHeight: 22 * 1.24,
      fontWeight: 'normal',
      fill: "#001166",
      align: "center",
      wordWrap: true,
      wordWrapWidth: 150
    });

    // Animation timeline
    this.currentTimeline = null;
    this.beginningTimeline = null;
  }

  async assetLoader() {
    await super.assetLoader();

    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }
  }

  async buildStage() {
    this.stage.eventMode = 'static';
    // Enable events on the whole stage
    this.stage.hitArea = this.screen;

    this.$screenEl = $(this.canvas).parents(".screen");

    this.questionTextContainer = this.$screenEl.find(".game-question");
    this.questionTextContainer.css({
      top: this.data.question.y,
      left: this.data.question.x,
    });

    // Prepare moving container
    this.movingContainer = new PIXI.Container();
    for (const [stepIndex, step] of this.sliderSteps.entries()) {
      // Prepare steps sprites
      const stepSprite = new PIXI.Sprite(this.assets[step.name + "_" + app.i18n.lang]);
      stepSprite.width = this.data.imageDimensions.width;
      stepSprite.height = this.data.imageDimensions.height;
      stepSprite.alpha = stepIndex === 0 ? 1.0 : 0.0;
      this.sliderStepSprites.push(stepSprite);
      this.movingContainer.addChild(stepSprite);
    }

    // Prepare the background container
    this.backgroundContainer = new PIXI.Container();
    this.backgroundContainer.position.x = this.appPadding;
    this.backgroundContainer.position.y = this.appPadding;
    this.backgroundContainer.mask = new PIXI.Graphics().rect(this.appPadding, this.appPadding, this.backgroundWidth, this.backgroundHeight).fill(0xffffff);
    this.backgroundContainer.addChild(this.movingContainer);

    // Prepare the slider container
    this.sliderContainer = new PIXI.Container();
    this.sliderBackgroundContainer = new PIXI.Container();
    this.sliderGraphic = new PIXI.Graphics().rect(0, 0, this.backgroundWidth, 20).fill({color: 0xCFD7FF});
    this.sliderBackgroundContainer.addChild(this.sliderGraphic);
    this.stage.addChild(this.backgroundContainer);

    // Prepare slider background progress
    this.sliderProgressGraphic = new PIXI.Graphics().rect(0, 0, this.backgroundWidth, 20).fill({color: 0xFFEB5A});
    this.sliderProgressGraphic.width = 0;
    this.sliderBackgroundContainer.addChild(this.sliderProgressGraphic);

    // Prepare slider handle
    // this.sliderHandleGraphic = new PIXI.Graphics().circle(0, 0, this.sliderHandleWidth).fill({color: 0xFFEB5A});
    this.sliderHandleGraphic = new PIXI.Sprite(this.assets["icone-slider"]);
    this.sliderHandleGraphic.anchor.set(0.5, 0.5);
    this.initialSliderHandlePos = {
      x: this.sliderHandleHalfWidth,
      y: this.sliderGraphic.height / 2
    }
    this.sliderHandleGraphic.position = {...this.initialSliderHandlePos};
    this.sliderHandleGraphic.eventMode = "static";
    this.sliderHandleGraphic.cursor = "pointer";
    this.sliderHandleGraphic.on("pointerdown", (e) => {
      this.handleOnDragStart(e);
    }).on("pointerup", (e) => {
      this.handleOnDragEnd(e);
    }).on("pointerupoutside", (e) => {
      this.handleOnDragEnd(e);
    });

    this.sliderContainer.position.x = this.appPadding;
    this.sliderContainer.position.y = this.appPadding + this.backgroundHeight + 62;
    this.sliderBackgroundContainer.mask = new PIXI.Graphics().roundRect(this.sliderContainer.position.x, this.sliderContainer.position.y, this.backgroundWidth, 20, 20).fill(0xffffff);
    this.sliderContainer.addChild(this.sliderBackgroundContainer);
    this.sliderContainer.addChild(this.sliderHandleGraphic);
    this.stage.addChild(this.sliderContainer);

    // Prepare slider labels container
    // Build slider labels
    this.sliderLabelsContainer = new PIXI.Container();
    for (const step of this.sliderSteps) {
      const labelTextEN = this.createInterfaceHTMLText(step.label.en, this.sliderLabelsTextStyle, "en");
      const labelTextFR = this.createInterfaceHTMLText(step.label.fr, this.sliderLabelsTextStyle, "fr");
      const labelPositionX = window.map(step.percentage, 0, 100, this.sliderHandleHalfWidth, this.sliderGraphic.width - this.sliderHandleHalfWidth);
      labelTextEN.anchor.set(0.5, 0);
      labelTextFR.anchor.set(0.5, 0);
      labelTextEN.position.x = labelPositionX;
      labelTextFR.position.x = labelPositionX;

      // Add vertical line
      const verticalLine = new PIXI.Sprite(this.assets["vertical-line"]);
      verticalLine.width = 3;
      verticalLine.height = 22;
      verticalLine.position.x = labelPositionX;
      verticalLine.position.y = -32;
      this.sliderLabelsContainer.addChild(verticalLine);

      this.sliderLabelsContainer.addChild(labelTextEN);
      this.sliderLabelsContainer.addChild(labelTextFR);
    }

    this.sliderLabelsContainer.position.x = this.appPadding;
    this.sliderLabelsContainer.position.y = this.sliderContainer.height + 55;
    this.stage.addChild(this.sliderLabelsContainer);

    // Add validation button
    const checkmark = new PIXI.Sprite(this.assets["checkmark"]);
    const checkmarkDisabled = new PIXI.Sprite(this.assets["checkmark-disabled"]);
    this.validateButton = new AppearingInteractiveButton(75, 75, checkmark, checkmarkDisabled, this.validateAnswers.bind(this), false);
    this.validateButton.position.x = this.data.question.x + this.questionTextContainer.width() / 2;
    this.validateButton.position.y = this.data.question.y + this.questionTextContainer.height() + checkmark.height + 20;
    this.stage.addChild(this.validateButton);

    // Used for debugging in pixijs devtool
    //globalThis.__PIXI_APP__ = this;
  }

  handleOnDragStart(e) {
    this.stage.eventMode = 'static';
    this.stage.on("pointermove", (e) => {
      this.handleOnDrag(e);
    });
  }

  handleOnDragEnd(e) {
    this.stage.eventMode = 'auto';
    this.stage.off("pointermove");

    // Find the closest handle step value
    let newHandleStep = this.sliderSteps[0].percentage;
    let newHandleIndex = 0;
    const handlePositionPerc = window.map(this.sliderHandleGraphic.position.x, this.sliderHandleHalfWidth, this.sliderGraphic.width - this.sliderHandleHalfWidth, 0, 100);
    for (const [stepIndex, step] of this.sliderSteps.entries()) {
      if (Math.abs(step.percentage - handlePositionPerc) < Math.abs(newHandleStep - handlePositionPerc)) {
        newHandleStep = step.percentage;
        newHandleIndex = stepIndex;
      }
    }

    // Step change
    if (this.currentHandleStep.index !== newHandleIndex) {
      const increased = this.currentHandleStep.index < newHandleIndex;
      this.currentHandleStep.percentage = newHandleStep;
      this.currentHandleStep.index = newHandleIndex;

      this.killTimeline();
      this.currentTimeline = gsap.timeline();

      // Handle steps visibility
      for (const [stepIndex, stepSprite] of this.sliderStepSprites.entries()) {
        if (stepIndex === this.currentHandleStep.index) {
          // Show step
          this.currentTimeline.to(stepSprite, 0.25, {
            alpha: 1.0
          }, 0.0);
        } else {
          // Hide step
          if (!increased && stepIndex !== 0) {
            this.currentTimeline.to(stepSprite, 0.25, {
              alpha: 0.0
            }, 0.0);
          }
        }
      }

      // Enable validation button on last step
      if (this.currentHandleStep.index === this.sliderSteps.length - 1) {
        this.validateButton.enable();
      } else {
        this.validateButton.disable();
      }
    }

    // Update handle position
    this.sliderHandleGraphic.position.x = window.map(newHandleStep, 0, 100, this.sliderHandleHalfWidth, this.sliderGraphic.width - this.sliderHandleHalfWidth);

    // Update slider background width
    this.sliderProgressGraphic.width = window.map(newHandleStep, 0, 100, this.sliderHandleHalfWidth, this.backgroundWidth);
  }

  handleOnDrag(e) {
    // Update handle position
    this.sliderHandleGraphic.position.x = Math.max(this.sliderHandleHalfWidth, Math.min(this.sliderGraphic.toLocal(e.global).x, this.sliderGraphic.width - this.sliderHandleHalfWidth));

    // Update slider background width
    this.sliderProgressGraphic.width = this.sliderHandleGraphic.position.x;
  }

  validateAnswers() {
    // Objective is on screen! Complete the challenge
    // app.animateOutGame();

    this.validateButton.hide();
    this.goToConclusion();
  }

  buildBegginingTimeline() {
    this.beginningTimeline = gsap.timeline({paused: true});

    // Slide labels
    // this.beginningTimeline.fromTo([this.sliderContainer, this.sliderLabelsContainer], {
    //   alpha: 0,
    // }, {
    //   alpha: 1,
    //   ease: "power1.inOut",
    //   duration: 0.50,
    //   immediateRender: true
    // });

    // Question text
    this.beginningTimeline.set(this.questionTextContainer, {
      alpha: 1.0
    });
    this.beginningTimeline.fromTo(this.questionTextContainer, {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.50,
      immediateRender: true
    });

    // Show validate button but disabled
    this.beginningTimeline.add(() => {
      this.validateButton.show(false);
    });
  }

  buildConclusionTimeline() {

    this.conclusionTimeline = gsap.timeline({paused: true});

    this.conclusionTimeline.addLabel("fade-out-question");

    this.conclusionTimeline.to(this.questionTextContainer, {
      alpha: 0.0,
      duration: 0.25,
      delay: 0.70,
      ease: "power1.inOut"
    }, "fade-out-question");

    // force game conclusion to be active
    this.conclusionTimeline.set(this.$screenEl.find(".game-conclusion"), {
      alpha: 1,
    });

    if (this.$screenEl.find(".container-title").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title"), {
        '--title-width': "0%"
      }, {
        '--title-width': "100%",
        duration: 0.45,
        ease: "power2.out",
        immediateRender: true
      });

      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title-inner"), {
        alpha: 0.0
      }, {
        alpha: 1.0,
        duration: 0.30,
        ease: "power1.in",
        immediateRender: true
      });
    }

    this.conclusionTimeline.fromTo(this.$screenEl.find('.container-content'), {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.30,
      immediateRender: true
    });

    this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
      y: "+=100%"
    }, {
      y: 0,
      duration: 1.50,
      ease: "power1.inOut",
      immediateRender: true
    }, "+=0.75");

    this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
      autoAlpha: 0
    }, {
      autoAlpha: 1,
      duration: 0.1,
      ease: "linear",
      immediateRender: true
    }, '<=');

    // Show container button
    if (this.$screenEl.find(".container-button").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-button"), {
        alpha: 0.0,
        scale: 0.0
      }, {
        alpha: 1.0,
        scale: 1.0,
        duration: 0.75,
        ease: "power2.out",
        immediateRender: true,
        onStart: () => {
          // Disable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").removeClass("play-animation");
        },
        onComplete: () => {
          this.$screenEl.find(".game-conclusion").removeClass("disabled");

          // Enable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").addClass("play-animation");
        }
      });

      // Disable click on this section
      this.$screenEl.find(".game-conclusion").addClass("disabled");
    }

  }

  goToConclusion() {
    this.conclusionTimeline.play(0);
  }

  beforeAnimateIn() {
    // rebuild timeline
    if (this.beginningTimeline) {
      this.beginningTimeline.pause();
    } else {
      this.buildBegginingTimeline();
    }

    if (this.conclusionTimeline) {
      this.conclusionTimeline.pause();
      // this.conclusionTimeline.progress(0, true);
    } else {
      this.buildConclusionTimeline();
    }
  }

  begin() {
    this.beginningTimeline.play(0);
  }

  killTimeline() {
    if (this.currentTimeline) {
      this.currentTimeline.kill();
      this.currentTimeline = null;
    }
  }

  reset() {
    //console.log("gameC Reset");

    // Kill timeline
    this.killTimeline();

    // Reset steps
    for (const [stepIndex, stepSprite] of this.sliderStepSprites.entries()) {
      stepSprite.alpha = (stepIndex !== 0) ? 0.0 : 1.0;
    }
    this.currentHandleStep = this.initialHandleStep;
    this.sliderProgressGraphic.width = 0;

    // Reset slider handle position
    this.sliderHandleGraphic.position = this.initialSliderHandlePos;
    this.validateButton.hide();

    // rebuild timeline
    if (this.beginningTimeline) {
      this.beginningTimeline.kill();
      this.beginningTimeline = null;
    }
    this.buildBegginingTimeline();

    if (this.conclusionTimeline) {
      this.conclusionTimeline.kill();
      this.conclusionTimeline = null;
    }
    this.buildConclusionTimeline();
  }

  interfaceLanguageSwitch(lang) {
    super.interfaceLanguageSwitch(lang);

    // const imageShown = lang === "fr" ? this.backgroundSpriteFR : this.backgroundSpriteEN;
    // const textHidden = lang === "fr" ? this.backgroundSpriteEN : this.backgroundSpriteFR;
    //
    // imageShown.alpha = 1;
    // textHidden.alpha = 0;
    for (const [stepIndex, step] of this.sliderSteps.entries()) {
      console.log(stepIndex, step.name);
      this.sliderStepSprites[stepIndex].texture = this.assets[`${step.name}_${lang}`];
    }

  }

}
